import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import AIChartTooltip from "../components/AIChartTooltip";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ZAxis,
  ResponsiveContainer, Cell,
} from "recharts";
import PlotSummary from "../components/PlotSummary";
import { useTheme, getColors } from "../ThemeContext";   // <-- added

import { API } from "../config/api";

const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525", border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d", pink:"#ff4da6", purple:"#a855f7",
  blue:"#3b9eff", red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }   // <-- added
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
const CHART_MARGIN = { top: 8, right: 24, left: -10, bottom: 36 };
/* Numeric ticks only: units live in axis titles to avoid overlap */
const SCATTER_CHART_MARGIN = { top: 8, right: 16, left: 8, bottom: 36 };

function getStatusColor() {               // <-- changed from frozen object to function
  return { operating: C.green, unloading: C.amber, closed: C.muted };
}
function Card({ title, subtitle, children, height, accentColor, summarySubtitle }) {
  const ac = accentColor || C.cyan;
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"20px 16px", position:"relative" }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${ac}88, transparent)` }}/>
      <div style={{ marginBottom:14 }}>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{subtitle}</div>}
      </div>
      {height ? <div style={{ height }}>{children}</div> : children}
      {height && (
        <PlotSummary inline title={title} subtitle={summarySubtitle || (typeof subtitle==="string" ? subtitle : "")} accent={ac}>{children}</PlotSummary>
      )}
    </div>
  );
}
function KPI({ label, value, unit, color }) {
  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"16px 18px" }}>
      <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:0.5 }}>{label}</div>
      <div style={{ fontFamily:MONO, fontSize:24, fontWeight:700, color:color||C.cyan, marginTop:6 }}>
        {value == null ? "-" : value}{unit ? <span style={{ fontSize:12, color:C.muted, marginLeft:4 }}>{unit}</span> : null}
      </div>
    </div>
  );
}

function ruleStatusColor(status) {
  return { pass:C.green, marginal:C.amber, fail:C.red }[status] || C.muted;
}
function RuleStatusIcon({ status, color }) {
  if (status === "pass") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  );
  if (status === "marginal") return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  );
}
function RulesPanel({ rules, accentColor }) {
  if (!rules || rules.length === 0) return null;
  const passCount = rules.filter(r=>r.status==="pass").length;
  const margCount = rules.filter(r=>r.status==="marginal").length;
  const failCount = rules.filter(r=>r.status==="fail").length;
  return (
    <Card title="Physics-Based Rules Validation" subtitle="Hard physical constraints checked against every cycle" accentColor={accentColor||C.orange}>
      <div style={{ display:"flex", gap:16, marginBottom:14 }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>◆ Pass: {passCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.amber }}>▲ Marginal: {margCount}</span>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.red }}>■ Fail: {failCount}</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {rules.map(r => {
          const sc = ruleStatusColor(r.status);
          return (
            <div key={r.id} style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:4 }}>
                <RuleStatusIcon status={r.status} color={sc}/>
                <span style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:C.text }}>{r.name}</span>
                <span style={{ fontFamily:MONO, fontSize:8, letterSpacing:1, textTransform:"uppercase", color:sc, background:`${sc}18`, border:`1px solid ${sc}44`, borderRadius:3, padding:"2px 7px" }}>{r.status}</span>
                {r.equation && <span style={{ fontFamily:MONO, fontSize:9, color:C.cyan, background:`${C.cyan}12`, border:`1px solid ${C.cyan}33`, borderRadius:3, padding:"2px 7px" }}>{r.equation}</span>}
                <span style={{ fontFamily:MONO, fontSize:10, color:C.cyan, marginLeft:"auto" }}>{r.value}</span>
              </div>
              <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, lineHeight:1.6 }}>{r.explanation}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const STATUS_COLOR = getStatusColor();   
  return (
    <div style={{ background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8, padding:"8px 12px", fontFamily:MONO, fontSize:11 }}>
      <div style={{ color:C.cyan, fontWeight:700, marginBottom:4 }}>{d.mandrel_id}</div>
      <div style={{ color:C.muted }}>Depth: <span style={{ color:C.text }}>{d.depth_ft} ft</span></div>
      <div style={{ color:C.muted }}>Status: <span style={{ color:STATUS_COLOR[d.status]||C.text }}>{d.status}</span></div>
      <div style={{ color:C.muted }}>Opening: <span style={{ color:C.text }}>{d.recommended_valve_opening_pct}%</span></div>
    </div>
  );
}

function selStyle() {                     
  return {
    background:C.panel, color:C.text, border:`1px solid ${C.border2}`, borderRadius:8,
    padding:"8px 12px", fontFamily:MONO, fontSize:11, cursor:"pointer",
  };
}
const badgeStyle = (color) => ({          // already a function taking a fresh `color` arg: fine as-is
  fontFamily:MONO, fontSize:9, color, background:`${color}18`, border:`1px solid ${color}44`,
  borderRadius:5, padding:"2px 8px", letterSpacing:0.5, textTransform:"uppercase",
});
function inputStyle() {                   // <-- changed from const object to function
  return {
    background:C.panel, color:C.text, border:`1px solid ${C.border2}`, borderRadius:6,
    padding:"4px 8px", fontFamily:MONO, fontSize:11, width:60,
  };
}
export default function MandrelValveManagerPage() {
  const { isDark } = useTheme();          
  applyTheme(getColors(isDark));          
  const STATUS_COLOR = getStatusColor();  
  const [wells, setWells] = useState([]);
  const [selectedWell, setSelectedWell] = useState("all");
  const [mandrels, setMandrels] = useState([]);
  const [physics, setPhysics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [drafts, setDrafts] = useState({}); 

  useEffect(() => {
    axios.get(`${API}/injection/wells`)
      .then(r => setWells(r.data.wells || []))
      .catch(() => setWells([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    Promise.all([
      axios.get(`${API}/mandrels`, { params: { well: selectedWell } }),
      axios.get(`${API}/mandrels/physics-validation`, { params: { well: selectedWell } }),
    ])
      .then(([r, p]) => {
        setMandrels(r.data.mandrels || []);
        const filteredPhysics = {
          ...p.data,
          rules: (p.data.rules || []).filter(rule => rule.name !== "Test-Rack Opening Pressure Decreases with Depth")
        };
        setPhysics(filteredPhysics);
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [selectedWell]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = mandrels.length;
    const operating = mandrels.filter(m => m.status === "operating").length;
    const unloading = mandrels.filter(m => m.status === "unloading").length;
    const closed = mandrels.filter(m => m.status === "closed").length;
    const avgOpening = total ? (mandrels.reduce((s,m)=>s+(m.recommended_valve_opening_pct||0),0)/total) : null;
    return { total, operating, unloading, closed, avgOpening: avgOpening!=null?Math.round(avgOpening*10)/10:null };
  }, [mandrels]);
  const byWell = useMemo(() => {
    const groups = {};
    for (const m of mandrels) {
      (groups[m.well_id] ||= []).push(m);
    }
    Object.values(groups).forEach(list => list.sort((a,b) => a.position - b.position));
    return groups;
  }, [mandrels]);

  async function saveMandrel(mandrelId, patch) {
    setSavingId(mandrelId);
    try {
      const res = await axios.patch(`${API}/mandrels/${encodeURIComponent(mandrelId)}`, patch);
      setMandrels(prev => prev.map(m => m.mandrel_id === mandrelId ? { ...m, ...res.data.mandrel } : m));
    } catch (e) {
      alert("Update failed: " + (e.response?.data?.error || e.message));
    } finally {
      setSavingId(null);
    }
  }

  function cycleStatus(current) {
    const order = ["closed", "unloading", "operating"];
    return order[(order.indexOf(current) + 1) % order.length];
  }

  return (
    <div style={{ fontFamily:SANS }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <select value={selectedWell} onChange={e=>setSelectedWell(e.target.value)} style={selStyle()}>
          <option value="all">All Wells</option>
          {wells.map(w => <option key={w.well_id} value={w.well_id}>{w.well_id}</option>)}
        </select>
      </div>

      {loading && <div style={{ color:C.muted, fontFamily:MONO }}>Loading…</div>}
      {error && <div style={{ color:C.red, fontFamily:MONO }}>{error}</div>}

      {!loading && !error && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:14, marginBottom:20 }}>
            <KPI label="Total Mandrels" value={stats.total} color={C.cyan}/>
            <KPI label="Operating" value={stats.operating} color={C.green}/>
            <KPI label="Unloading" value={stats.unloading} color={C.amber}/>
            <KPI label="Closed" value={stats.closed} color={C.muted}/>
            <KPI label="Avg Opening" value={stats.avgOpening} unit="%" color={C.purple}/>
          </div>

          <Card title="Valve Depth Map" subtitle="Depth (ft) vs recommended opening (%), colored by status" summarySubtitle="Gas-lift mandrel valves plotted by depth (ft, shallowest at top) against recommended valve opening (%); each point is one mandrel valve, colored by status: operating, unloading or closed. Judge whether opening decreases with depth as expected for unloading, and which valves are open or closed" accentColor={C.cyan} height={320}>
            <ResponsiveContainer width="100%" height={280}>
              <ScatterChart margin={SCATTER_CHART_MARGIN}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3"/>
                <XAxis
                  type="number"
                  dataKey="recommended_valve_opening_pct"
                  name="Opening"
                  domain={[0, 100]}
                  tickCount={6}
                  tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                  tickLine={false}
                  tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                  label={{ value:"Recommended Valve Opening (%)", position:"insideBottom", offset:-18, fill:C.muted, fontSize:9, fontFamily:MONO }}
                />
                <YAxis
                  type="number"
                  dataKey="depth_ft"
                  name="Depth"
                  reversed
                  width={48}
                  tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (typeof v === "number" ? Math.round(v) : v)}
                  label={{
                    value: "Depth (ft)",
                    angle: -90,
                    position: "insideLeft",
                    dx: -4,
                    dy: 20,
                    fill: C.muted,
                    fontSize: 9,
                    fontFamily: MONO,
                  }}
                />
                <ZAxis range={[80,80]}/>
                <Tooltip
                  trigger="hover"
                  isAnimationActive={false}
                  content={<AIChartTooltip fieldKey="recommended_valve_opening_pct" contextData={{ chart:"Valve Depth Map" }} />}
                />
                <Scatter data={mandrels} isAnimationActive={false}>
                  {mandrels.map((m,i)=><Cell key={i} fill={STATUS_COLOR[m.status]||C.cyan}/>)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
            <div
              aria-label="Valve status legend"
              style={{
                display:"flex",
                flexWrap:"wrap",
                alignItems:"center",
                justifyContent:"center",
                gap:"10px 18px",
                marginTop:8,
                fontFamily:MONO,
                fontSize:10,
                color:C.text,
              }}
            >
              {[
                { label:"Operating", color:STATUS_COLOR.operating },
                { label:"Unloading", color:STATUS_COLOR.unloading },
                { label:"Closed", color:STATUS_COLOR.closed },
              ].map(item => (
                <div key={item.label} style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      display:"inline-block",
                      width:9,
                      height:9,
                      borderRadius:"50%",
                      background:item.color,
                      boxShadow:`0 0 6px ${item.color}66`,
                      flexShrink:0,
                    }}
                  />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </Card>

          <div style={{ marginTop:18, display:"flex", flexDirection:"column", gap:18 }}>
            {Object.entries(byWell).map(([wellId, list]) => (
              <Card key={wellId} title={wellId} subtitle={`${list.length} mandrel${list.length!==1?"s":""}: shallowest to deepest`} accentColor={C.teal}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1.4fr 0.8fr 0.9fr 0.9fr 0.9fr 1fr 1.1fr", gap:8, fontFamily:MONO, fontSize:9, color:C.muted, textTransform:"uppercase", padding:"0 10px" }}>
                    <span>Mandrel</span><span>Depth</span><span>Valve Type</span><span>Port (64th")</span><span>Test Rack</span><span>Status</span><span>Opening %</span>
                  </div>
                  {list.map(m => {
                    const draft = drafts[m.mandrel_id] ?? m.recommended_valve_opening_pct;
                    const dirty = draft !== m.recommended_valve_opening_pct;
                    return (
                      <div key={m.mandrel_id} style={{
                        display:"grid", gridTemplateColumns:"1.4fr 0.8fr 0.9fr 0.9fr 0.9fr 1fr 1.1fr", gap:8,
                        alignItems:"center", background:C.panel, border:`1px solid ${C.border}`, borderRadius:8,
                        padding:"10px 10px", fontFamily:MONO, fontSize:11,
                      }}>
                        <span style={{ color:C.text }}>{m.mandrel_id} <span style={{ color:C.muted }}>#{m.position}</span></span>
                        <span style={{ color:C.text }}>{m.depth_ft} ft</span>
                        <span style={{ color:C.muted }}>{m.valve_type}</span>
                        <span style={{ color:C.muted }}>{m.port_size_64th_in}</span>
                        <span style={{ color:C.muted }}>{m.test_rack_opening_psi} psi</span>
                        <span>
                          <button
                            onClick={() => saveMandrel(m.mandrel_id, { status: cycleStatus(m.status) })}
                            disabled={savingId === m.mandrel_id}
                            style={{ ...badgeStyle(STATUS_COLOR[m.status]||C.muted), cursor:"pointer", border:`1px solid ${STATUS_COLOR[m.status]||C.muted}66` }}
                            title="Click to cycle status: closed → unloading → operating"
                          >
                            {savingId === m.mandrel_id ? "…" : m.status}
                          </button>
                        </span>
                        <span style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <input
                            type="number" min={0} max={100} step={0.5}
                            value={draft}
                            onChange={e => setDrafts(d => ({ ...d, [m.mandrel_id]: e.target.value }))}
                            style={inputStyle()}
                          />
                          {dirty && (
                            <button
                              onClick={() => saveMandrel(m.mandrel_id, { recommended_valve_opening_pct: Number(draft) })}
                              disabled={savingId === m.mandrel_id}
                              style={{ ...badgeStyle(C.cyan), cursor:"pointer" }}
                            >
                              Save
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
            {Object.keys(byWell).length === 0 && (
              <div style={{ color:C.muted, fontFamily:MONO, fontSize:12 }}>No mandrel data for this selection.</div>
            )}
          </div>

          <div style={{ marginTop:18 }}>
            <RulesPanel rules={physics?.rules} accentColor={C.orange}/>
          </div>
        </>
      )}
    </div>
  );
}